import isString from 'lodash-es/isString';
import { compareColors, tryParseColor } from '../color';
import { isBlockLevelContent, isPhrasingContent } from '../dom/content';
import { getElementContents, simplifyAttributes, simplifyElement } from '../dom/element';
import {
  findAncestor,
  isElement,
  isElementWithTagName,
  isHTMLOrSVGElement,
  isText,
  isWhitespaceText,
} from '../dom/node';
import { paintNodesInRange } from '../dom/paint';
import { isBreakElement, isParagraphElement, isParagraphEmpty } from '../dom/paragraph';
import { someInIterable } from '../iterable';
import { getNumericFontWeight } from './bold';
import { getCascadingComputedStyle, getComputedStyle } from './computedStyle';

const MAX_WIDTH = 600; // px

const REMOVE_PROPERTIES = [
  // Because these would be bad UX.
  'user-select',
  'cursor',
  // To avoid wrapping and overflow issues.
  'overflow',
  'overflow-x',
  'overflow-y',
  'overflow-wrap',
  'word-break',
  // Because these are not widely supported.
  'widows',
  'orphans',
  '-webkit-text-stroke-width',
  '-webkit-font-smoothing',
];

const REMOVE_TAG_PROPERTIES = [
  ['TBODY', 'box-sizing'],
  ['THEAD', 'box-sizing'],
];

// TODO: Complete this list.
const COMPARE_PROPERTIES = [
  'text-decoration',
  'box-sizing',
  'font-style',
  'font-variant-ligatures',
  'font-variant-caps',
  'font-variant-numeric',
  'font-variant-asian',
  'font-variant-east-asian',
  'font-stretch',
  'letter-spacing',
  'dir',
  'text-align',
  'text-indent',
  'text-transform',
  'white-space',
  'word-spacing',
  'direction',
  'caption-side',
];

// TODO: Complete this list.
const COMPARE_COLOR_PROPERTIES: { [key: string]: string } = {
  color: 'black',
};

// TODO: Complete this list.
const REMOVE_DEFAULT_PROPERTIES: { [key: string]: string | RegExp } = {
  'background-image': 'none',
  'background-position': /^[^1-9a-z]+$/i,
  'background-size': 'auto',
  'background-repeat': 'repeat',
  'background-color': /\b(initial|transparent)\b|\brgba\([^1-9]+\)/,
  'background-attachment': 'scroll',
  'background-origin': 'padding-box',
  'background-clip': 'border-box',
  'border-radius': /^[^1-9]+$/i,
  'box-shadow': 'none',
  'break-before': 'auto',
  'break-inside': 'auto',
  'break-after': 'auto',
  'vertical-align': /\b(initial|baseline)\b/,
  clear: 'none',
  float: 'none',
};

export function simplifyStyleInRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node =>
      isElement(node) && (isElementWithTagName(node, 'font') || node.hasAttribute('style')),
    paintElement: element => {
      const convertedElement = maybeConvertFontElement(element);

      if (isHTMLOrSVGElement(convertedElement) && convertedElement.hasAttribute('style')) {
        simplifyStyleInElement(container, convertedElement);
        simplifyAttributes(convertedElement);

        if (convertedElement.tagName === 'SPAN') {
          return simplifyElement(convertedElement);
        }
      }

      return convertedElement;
    },
  });
}

// TODO: Clean up.
export function simplifyStyleInElement(container: Node, element: HTMLElement | SVGElement): void {
  const inlineCss = element.getAttribute('style');
  if (!inlineCss) {
    return;
  }

  const cascadingStyles = getCascadingComputedStyle(element, container);
  if (cascadingStyles.length < 2) {
    return;
  }

  // Defer independent changes to the end, so we don't invalidate the browser's style caches unnecessarily.
  const deferredChanges: Array<() => void> = [];
  const deferChange = (callback: () => void) => {
    deferredChanges.push(callback);
  };

  /* eslint-disable no-param-reassign */

  // Remove all text-decoration longhand properties, because they aren't widely supported.
  const { textDecorationLine } = element.style;
  element.style.removeProperty('text-decoration-line');
  element.style.removeProperty('text-decoration-style');
  element.style.removeProperty('text-decoration-color');
  if (!textDecorationLine || (!isElementWithTagName(element, 'a') && textDecorationLine === 'none')) {
    element.style.removeProperty('text-decoration');
  } else if (textDecorationLine) {
    element.style.textDecoration = textDecorationLine;
  }

  // Constrain excessive absolute widths — these usually result from percentage widths turned into absolute widths
  // during past copy/paste operations.
  if (
    element.style.width &&
    element.style.width.endsWith('px') &&
    parseFloat(element.style.width) > MAX_WIDTH
  ) {
    deferChange(() => {
      element.style.width = '100%';
    });
  }

  const style = cascadingStyles[0];
  const inheritedStyle = cascadingStyles[1];

  Object.keys(REMOVE_DEFAULT_PROPERTIES).forEach(name => {
    const value = element.style.getPropertyValue(name);
    const defaultValue = REMOVE_DEFAULT_PROPERTIES[name];
    const isMatch = isString(defaultValue)
      ? value.toLowerCase() === defaultValue.toLowerCase()
      : value.match(defaultValue);
    if (isMatch && !element.style.getPropertyPriority(name)) {
      element.style.removeProperty(name);
    }
  });

  // Remove background-repeat when there is no background.
  if (element.style.backgroundRepeat && !element.style.background && !element.style.backgroundImage) {
    element.style.backgroundRepeat = '';
  }

  // Remove the most common browser default preset.
  if (
    element.style.fontFamily &&
    element.style.fontFamily.includes('Times') &&
    element.style.fontSize === 'medium'
  ) {
    element.style.fontFamily = '';
    element.style.fontSize = '';
  }

  if (element.style.fontSize && style.fontSize && inheritedStyle.fontSize) {
    // Remove font sizes that are immediately overridden with an absolute size — we've seen cases of 1px font size wrappers.
    const isSuperfluous =
      isSimpleTextElement(element) &&
      !isTrivialParagraph(element) &&
      !hasDescendant(
        element,
        childNode => isNonTrivialText(childNode),
        childElement => !!childElement.style.fontSize && !childElement.style.fontSize.match(/(%|em)$/i),
      );

    // Calculate the relative between the font sizes.
    const fontSize = parseFloat(style.fontSize);
    const inheritedFontSize = parseFloat(inheritedStyle.fontSize);
    const distance = Math.abs(fontSize - inheritedFontSize) / inheritedFontSize;

    // Simplify font sizes in paragraphs and list items more aggressively than elsewhere.
    const flowAncestor = findAncestor(element, isBlockLevelContent, container);
    const distanceThreshold = isSimpleTextElement(flowAncestor) ? 0.09 : 0.04;

    if (distance < distanceThreshold || isSuperfluous) {
      deferChange(() => {
        element.style.fontSize = '';
      });
    }
  }

  // Remove non-standard font families.
  if (element.style.fontFamily) {
    element.style.fontFamily = element.style.fontFamily.replace(/-webkit-standard\s*,?/g, '');
  }

  if (element.style.fontFamily && style.fontFamily && inheritedStyle.fontFamily) {
    // Remove font families that are immediately overridden.
    const isSuperfluous =
      !isTrivialParagraph(element) &&
      !hasDescendant(
        element,
        childNode => isNonTrivialText(childNode),
        childElement => !!childElement.style.fontFamily && childElement.style.fontFamily !== 'inherit',
      );

    const flowAncestor = findAncestor(element, isBlockLevelContent, container);
    const minMatchCount = isSimpleTextElement(flowAncestor) ? 2 : 3;
    const fontFamily = getGenericFontFamily(style.fontFamily, minMatchCount);
    const inheritedFontFamily = getGenericFontFamily(inheritedStyle.fontFamily, minMatchCount);

    if (fontFamily === inheritedFontFamily || isSuperfluous) {
      deferChange(() => {
        element.style.fontFamily = '';
      });
    } else if (!GENERIC_FONT_FAMILIES.has(style.fontFamily) && style.fontFamily.split(',').length === 1) {
      // If only one font family is specified and it's not a generic font family, add a sans-serif fallback.
      deferChange(() => {
        element.style.fontFamily += ', sans-serif';
      });
    }
  }

  if (element.style.fontWeight && style.fontWeight && inheritedStyle.fontWeight) {
    const fontWeight = getNumericFontWeight(style.fontWeight);
    const inheritedFontWeight = getNumericFontWeight(inheritedStyle.fontWeight);

    if (fontWeight === inheritedFontWeight) {
      deferChange(() => {
        element.style.fontWeight = '';
      });
    }
  }

  Object.keys(COMPARE_COLOR_PROPERTIES).forEach(name => {
    // Remove colors that are immediately overridden in all children, we've seen this on links.
    const isSuperfluous =
      !isTrivialParagraph(element) &&
      !hasDescendant(
        element,
        childNode => isNonTrivialText(childNode),
        childElement => childElement.tagName === 'A' || !!childElement.style.getPropertyValue(name),
      );
    if (isSuperfluous) {
      element.style.removeProperty(name);
      return;
    }

    if (element.style.getPropertyValue(name) && !element.style.getPropertyPriority(name)) {
      const color = tryParseColor(style.getPropertyValue(name));
      let inheritedColor = tryParseColor(inheritedStyle.getPropertyValue(name));
      if (!inheritedColor || inheritedColor.alpha() === 0) {
        const defaultValue = COMPARE_COLOR_PROPERTIES[name];
        inheritedColor = tryParseColor(defaultValue);
      }

      if (color && inheritedColor) {
        // Require pure grays to be further apart to keep them around. This conveniently covers Old Front gray.
        const minDistance = color.saturationl() === 0 && inheritedColor.saturationl() === 0 ? 8.5 : 5;
        const distance = compareColors(color, inheritedColor);
        if (distance < minDistance) {
          // Use 'inherit' for colors on links, because they typically have a user agent style.
          if (isElementWithTagName(element, 'a') && name === 'color') {
            element.style.setProperty('color', 'inherit');
          } else {
            element.style.removeProperty(name);
          }
        }
      }
    }
  });

  // Simplify background colors, not based on style cascading, but based on ancestors with background colors.
  if (
    element.style.getPropertyValue('background-color') &&
    !element.style.getPropertyPriority('background-color')
  ) {
    const backgroundColor = tryParseColor(element.style.getPropertyValue('background-color'));
    const ancestorWithBackgroundColor = findAncestor(
      element,
      (ancestor => {
        if (
          ancestor === element ||
          !isHTMLOrSVGElement(ancestor) ||
          !ancestor.style.getPropertyValue('background-color')
        ) {
          return false;
        }

        const ancestorBackgroundColor = tryParseColor(ancestor.style.getPropertyValue('background-color'));
        return !!ancestorBackgroundColor && ancestorBackgroundColor.alpha() > 0;
      }) as typeof isHTMLOrSVGElement,
      container,
    );
    const upstreamBackgroundColor = ancestorWithBackgroundColor
      ? tryParseColor(ancestorWithBackgroundColor.style.getPropertyValue('background-color'))
      : tryParseColor('white');

    if (backgroundColor && upstreamBackgroundColor) {
      const distance = compareColors(backgroundColor, upstreamBackgroundColor);
      if (distance < 1) {
        element.style.removeProperty('background-color');
      }
    }
  }

  // Handle the white-space property. We need to support pre-wrap for Google Docs pastes.
  const whiteSpace = element.style.getPropertyValue('white-space');
  const textContent = element.textContent || '';
  if (whiteSpace && !textContent.match(/(^ | {2}| $)/)) {
    // Remote the white-space property if the text content has trivial whitespace.
    element.style.removeProperty('white-space');
  } else if (whiteSpace === 'nowrap') {
    // Allow wrapping.
    element.style.setProperty('white-space', 'normal');
  } else if (whiteSpace === 'pre') {
    // Allow wrapping.
    element.style.setProperty('white-space', 'pre-wrap');
  }

  COMPARE_PROPERTIES.forEach(name => {
    // Skip text decoration on links, because they typically have a user agent style.
    if (isElementWithTagName(element, 'a') && name.startsWith('text-decoration')) {
      return;
    }

    if (element.style.getPropertyValue(name) && !element.style.getPropertyPriority(name)) {
      const value = style.getPropertyValue(name);
      const inheritedValue = inheritedStyle.getPropertyValue(name);

      if (value === inheritedValue) {
        deferChange(() => {
          element.style.removeProperty(name);
        });
      }
    }
  });

  if (element.tagName === 'SPAN' && element.style.display === 'inline') {
    deferChange(() => {
      element.style.removeProperty('display');
    });
  }

  REMOVE_TAG_PROPERTIES.forEach(([tagName, propertyName]) => {
    if (element.tagName !== tagName) {
      return;
    }

    element.style.removeProperty(propertyName);
  });

  REMOVE_PROPERTIES.forEach(name => {
    element.style.removeProperty(name);
  });

  deferredChanges.forEach(deferredChange => deferredChange());

  /* eslint-enable no-param-reassign */
}

const SIMPLE_TEXT_TAG_NAMES = new Set(['DIV', 'P', 'UL', 'OL', 'LI']);

function isSimpleTextElement(node: Node | null) {
  return isElement(node) && (SIMPLE_TEXT_TAG_NAMES.has(node.tagName) || isPhrasingContent(node));
}

function isTrivialParagraph(node: Node | null): boolean {
  return (
    (isParagraphElement(node) || isElementWithTagName(node, 'p')) &&
    (isParagraphEmpty(node) || (node.childNodes.length === 1 && isBreakElement(node.firstChild)))
  );
}

function isNonTrivialText(node: Node | null): boolean {
  if (!isText(node)) {
    return false;
  }

  // If this is not an only child, look for ANY whitespace including non-breaking spaces.
  if (node.previousSibling || node.nextSibling) {
    return !node.data.match(/^\s*$/);
  }

  // If this is an only child, look for collapsible whitspace, to account for filler.
  return !isWhitespaceText(node);
}

function hasDescendant(
  element: Element,
  predicate: (node: Node) => boolean,
  skipPredicate: (element: HTMLElement | SVGElement) => boolean,
): boolean {
  return someInIterable(element.childNodes, childNode => {
    if (predicate(childNode)) {
      return true;
    }

    if (isHTMLOrSVGElement(childNode)) {
      return !skipPredicate(childNode) && hasDescendant(childNode, predicate, skipPredicate);
    }

    return false;
  });
}

const FONT_ATTRIBUTES = new Set(['face', 'size', 'color']);

function maybeConvertFontElement(element: Element): Element {
  const { ownerDocument: document } = element;
  if (!document || !isElementWithTagName(element, 'font')) {
    return element;
  }

  // We'll need this to determine the pixel font size.
  const computedStyle = getComputedStyle(element, element);

  // Create the replacement element.
  const newElement = document.createElement('span');

  // Copy over non-font attributes.
  const { attributes } = element;
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (!FONT_ATTRIBUTES.has(attribute.name)) {
      element.setAttribute(attribute.name, attribute.value);
    }
  }

  // Copy over the contents.
  newElement.appendChild(getElementContents(element));

  // Translate the face attribute.
  const face = element.getAttribute('face');
  if (face) {
    newElement.style.setProperty('font-family', face);
  }

  // Translate the size attribute.
  const size = element.getAttribute('size');
  if (size && computedStyle) {
    newElement.style.setProperty('font-size', computedStyle.fontSize);
  }

  // Translate the color attribute.
  const color = element.getAttribute('color');
  if (color) {
    newElement.style.setProperty('color', color);
  }

  return newElement;
}

const GENERIC_FONT_FAMILIES = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy']);

/** Font families that we'll immediately match as sans-serif. */
const PRIMARY_SANS_SERIF_FONT_FAMILIES = new Set(['helvetica', 'helvetica neue', 'arial']);

/** Font families that will count towards sans-serif. */
const SECONDARY_SANS_SERIF_FONT_FAMILIES = new Set([
  'verdana',
  // https://front.atlassian.net/browse/PB-32275
  // 'calibri',
  'roboto',
  '-apple-system',
  'system-ui',
  'segoe ui',
  'ubuntu',
  'sans-serif',
]);

function getGenericFontFamily(fontFamily: string, minMatchCount = 2): string {
  let sansSerifCount = 0;
  let nonSansSerifGenericFontFamily: string | undefined;

  fontFamily
    .split(',')
    // Trim whitespace and quotes surrounding the font family names.
    .map(name => name.replace(/(^\s*['"]?\s*|\s*['"]?\s*$)/g, '').toLowerCase())
    .forEach(name => {
      if (PRIMARY_SANS_SERIF_FONT_FAMILIES.has(name)) {
        sansSerifCount += Infinity;
      } else if (SECONDARY_SANS_SERIF_FONT_FAMILIES.has(name)) {
        sansSerifCount += 1;
      } else if (GENERIC_FONT_FAMILIES.has(name)) {
        nonSansSerifGenericFontFamily = name;
      }
    });

  // Except for Helvetica/Arial, require at least two sans-serif matches, because we're using sans-serif by itself as a
  // fallback for any font.
  if (sansSerifCount >= minMatchCount) {
    return 'sans-serif';
  }

  if (nonSansSerifGenericFontFamily) {
    return nonSansSerifGenericFontFamily;
  }

  return fontFamily;
}
